import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Loader2, Eye, EyeOff, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { useToast } from '@/hooks/use-toast';
import { MIN_PASSWORD_LENGTH } from '@/context/AuthContext';

// How long to wait for detectSessionInUrl to absorb the recovery hash before
// declaring the link invalid.
const SESSION_WAIT_MS = 3000;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // null = still waiting for the recovery session to appear
  const [hasSession, setHasSession] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    // detectSessionInUrl may have absorbed the recovery hash before this
    // component mounted, so check for an existing session first…
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (active && session) setHasSession(true);
    });

    // …and also subscribe for the PASSWORD_RECOVERY / SIGNED_IN event in case
    // the hash is processed after mount.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY' || session) setHasSession(true);
    });

    const timer = setTimeout(() => {
      if (active) setHasSession((prev) => (prev === null ? false : prev));
    }, SESSION_WAIT_MS);

    return () => {
      active = false;
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setIsLoading(false);

    if (updateError) {
      logger.error('updateUser(password) failed', updateError, { component: 'ResetPasswordPage' });
      setError(updateError.message || 'Failed to update password');
      return;
    }

    toast({ title: 'Password updated' });
    navigate('/crm/dashboard', { replace: true });
  };

  // Still waiting for the recovery session
  if (hasSession === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground text-sm">Verifying your reset link…</p>
        </div>
      </div>
    );
  }

  // Landed with no recovery session: expired or invalid link
  if (!hasSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="backdrop-blur-xl shadow-2xl rounded-3xl">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-16 w-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
              <h1 className="text-xl font-semibold">Link expired or invalid</h1>
              <p className="text-sm text-muted-foreground">
                This password reset link is no longer valid. Request a new one
                and try again.
              </p>
              <Button asChild className="w-full h-12">
                <Link to="/forgot-password">Request a new link</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">

        <Card className="backdrop-blur-xl shadow-2xl rounded-3xl">
          <CardHeader className="items-center text-center gap-4 pb-2">
            <div>
              <CardTitle className="text-2xl">Choose a New Password</CardTitle>
              <CardDescription className="mt-1">
                Enter and confirm your new password
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Lock className="w-5 h-5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    className="pl-11 pr-11 h-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Must be at least {MIN_PASSWORD_LENGTH} characters
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="w-5 h-5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    required
                    className="pl-11 h-12"
                  />
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription className="text-center">{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={
                  isLoading ||
                  password.length < MIN_PASSWORD_LENGTH ||
                  password !== confirmPassword
                }
                className="w-full h-12 text-base"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none opacity-50" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl pointer-events-none opacity-30" />
      </div>
    </div>
  );
}

export default ResetPasswordPage;
